import type { SelectHTMLAttributes } from 'react';

type Option = string | { value: string; label: string };

export interface SelectProps extends SelectHTMLAttributes<HTMLSelectElement> {
  label?: string;
  hint?: string;
  options: Option[];
  placeholder?: string;
}

function normalize(opt: Option): { value: string; label: string } {
  return typeof opt === 'string' ? { value: opt, label: opt } : opt;
}

export function Select({
  label,
  hint,
  options,
  placeholder,
  required,
  className = '',
  value,
  defaultValue,
  ...rest
}: SelectProps) {
  // Only seed the empty placeholder as defaultValue when uncontrolled.
  const uncontrolled = value === undefined && defaultValue === undefined;
  const resolvedDefault = placeholder && uncontrolled ? '' : defaultValue;
  const select = (
    <select
      className="tr-select"
      required={required}
      value={value}
      defaultValue={resolvedDefault}
      {...rest}
    >
      {placeholder && (
        <option value="" disabled>
          {placeholder}
        </option>
      )}
      {options.map((o) => {
        const { value, label: l } = normalize(o);
        return (
          <option key={value} value={value}>
            {l}
          </option>
        );
      })}
    </select>
  );

  // Bare select (drops into a caller-provided field wrapper, e.g. LeadIQ toolbar).
  if (!label) return select;

  return (
    <label className={`tr-field ${className}`.trim()}>
      <span className="tr-field__label">
        {label}
        {required && <span className="tr-field__req">*</span>}
      </span>
      <div className="tr-field__control">{select}</div>
      {hint && <span className="tr-field__hint">{hint}</span>}
    </label>
  );
}
